import { differenceInCalendarDays } from "date-fns";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnAOverviewDTO } from "../../../../models/tna-overviews";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { getNormaleDateOrMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAOverviewsData: (pageSize?: number) => Promise<LoadResponse<TnAOverviewDTO> | undefined>;
  allItems: () => TnAOverviewDTO[];
}

const ExportTnAOverviewToExcel = ({ disabled, loadEmployeeGroup, loadTnAOverviewsData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_dashboard";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Time and attendance overviews"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
      ];
    },
    [legalEntityOption?.legalEntityIds, selectedLegalEntity?.name, employeeGroupOption, onlyCurrentEmployeesOptionValue]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.pendingApprovals`)}`,
      `${t(`${translationFields}.workedTimeBalance`)}`,
      `${t(`${translationFields}.vacationBalance`)} ${new Date().getFullYear()}`,
      `${t(`${translationFields}.timesheetAnomalies`)}`,
      `${t(`${translationFields}.abnormalTime`)}`,
      `${t(`${translationFields}.lastSignOff`)}`,
      `${t(`${translationFields}.timesheet_usage_information`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (allItems: TnAOverviewDTO[]) => {
      return allItems.map((item) => {
        const absences = item.pendingApprovals.length ? `${item.pendingApprovals.length} abs` : "";
        const extraHours = item.pendingEHApprovals.length ? `${item.pendingEHApprovals.length} eh` : "";
        const pendingApprovals =
          !!absences && !!extraHours
            ? { value: `${absences}, ${extraHours}` }
            : absences
            ? { value: absences }
            : extraHours
            ? { value: extraHours }
            : { value: " " };

        const diffInCalendarDays = differenceInCalendarDays(new Date(), new Date(item?.lastUsage as Date));

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.pendingApprovals`)}`]: pendingApprovals,
          [`${t(`${translationFields}.workedTimeBalance`)}`]: {
            value: item?.workedTimeBalance ? item?.workedTimeBalance?.toFixed(2) : " ",
          },
          [`${t(`${translationFields}.vacationBalance`)} ${new Date().getFullYear()}`]: {
            value: item?.vacationBalance ? item?.vacationBalance?.toFixed(2) : " ",
          },
          [`${t(`${translationFields}.timesheetAnomalies`)}`]: {
            value: item?.timesheetAnomalies ? item?.timesheetAnomalies : " ",
          },
          [`${t(`${translationFields}.abnormalTime`)}`]: { value: item?.abnormalTime ? item?.abnormalTime : " " },
          [`${t(`${translationFields}.lastSignOff`)}`]: {
            value: !item.tnAOk
              ? "Tool not activated"
              : !item.lastTimesheetConfirmation
              ? "Never"
              : `${intlFormatDateShort(new Date(item.lastTimesheetConfirmation))}`,
          },
          [`${t(`${translationFields}.timesheet_usage_information`)}`]: {
            value: !item.tnAOk
              ? "Tool not activated"
              : item.lastUsage
              ? diffInCalendarDays === 0
                ? `${t(`${tRP}.timesheet_usage_information_text.today`)}`
                : diffInCalendarDays === 1
                ? `${t(`${tRP}.timesheet_usage_information_text.yesterday`)}`
                : `${diffInCalendarDays} ${t(`${tRP}.timesheet_usage_information_text.days_ago`)}`
              : "Never",
          },
          sortDirection: headerNames,
        };
      });
    },
    [headerNames]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } TnA Overviews`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnAOverviewsData();

        if (firstResponse?.error) return;

        const allTnAOverviewData = await loadTnAOverviewsData(firstResponse?.totalItems);

        if (!!allTnAOverviewData?.error || !allTnAOverviewData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnAOverviewData?.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } TnA Overviews`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnAOverviewsData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTnAOverviewToExcel;
